import { FormsModule } from '@angular/forms';
import { Component, Inject, OnInit } from '@angular/core';
import { MailFolderContract } from '@app-types/api/mail-folder';
import { MailFolderService } from '@app-services/mail-folder/mail.folder.service';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MailFolderSelectDialogComponent } from '../mail-account-folders/mail-folder-select-dialog/mail-folder-select-dialog.component';
import { MailAccountGetByOrganizationIdContract } from '@app-types/api/mail-account';
import { MailAccountService } from '@app-services/mail-account/mail.account.service';
import { EmailClient } from '@app-services/api/clients/email-client';
import { MailFolderType } from 'src/app/types/enums/mail-folder.type';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { sortByTextField } from 'src/app/shared/utils/sortData';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SomethingWentWrongComponent } from '../common/error/something-went-wrong/something-went-wrong.component';
import { ErrorHintComponent } from '../common/error-hint/error-hint.component';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingButtonComponent } from '../common/loading-button/loading-button.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import {
  SearchableSelectComponent,
  SearchableSelectOption,
} from '../common/searchable-select/searchable-select.component';

@Component({
  selector: 'app-reassign-dialog',
  templateUrl: './reassign-dialog.component.html',
  styleUrls: ['./reassign-dialog.component.scss'],
  imports: [
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    SomethingWentWrongComponent,
    ErrorHintComponent,
    TranslateModule,
    LoadingButtonComponent,
    NgxSkeletonLoaderModule,
    SearchableSelectComponent,
    FormsModule,
  ],
})
export class ReassignDialogComponent implements OnInit {
  public isLoading = true;
  public isLoadingFolders = false;
  public cannotLoad = false;
  public saving = false;
  public cannotSave = false;

  public folders: MailFolderContract[] = [];
  public accounts: MailAccountGetByOrganizationIdContract[] = [];
  public accountOptions: SearchableSelectOption<number>[] = [];
  public selectedFolderId?: number;
  public selectedMailAccountId: number | null = null;

  constructor(
    public matchError: MatchError,
    private mailFolderService: MailFolderService,
    private mailAccountService: MailAccountService,
    public dialogRef: MatDialogRef<MailFolderSelectDialogComponent>,
    private emailClient: EmailClient,
    @Inject(MAT_DIALOG_DATA)
    public data: { mailAccountId: number; ids: number[] }
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  async loadData(): Promise<void> {
    this.cannotLoad = false;
    try {
      await this.loadAccounts(this.data.mailAccountId);
      if (this.selectedMailAccountId) {
        await this.loadFolders(this.selectedMailAccountId);
      }
    } catch (e) {
      this.cannotLoad = true;
      this.matchError.logError(e);
    } finally {
      this.isLoading = false;
    }
  }

  async loadFolders(mailAccountId: number): Promise<void> {
    try {
      this.isLoadingFolders = true;
      this.folders =
        await this.mailFolderService.getFoldersForAccountReassign(
          mailAccountId
        );
      if (this.folders.length) {
        this.selectedFolderId = this.folders.find(
          e => e.folderType === MailFolderType.Inbox && e.creationType === 1
        )?.mailFolderId;
      }
    } catch (error) {
      this.matchError.errorHandler(error);
      this.cannotLoad = true;
    } finally {
      this.isLoadingFolders = false;
    }
  }

  async loadAccounts(mailAccountId: number): Promise<void> {
    this.accounts = sortByTextField(
      await this.mailAccountService.getAccountsForAccountOrganization(
        mailAccountId
      ),
      'name'
    );
    this.accountOptions = this.accounts.map(account => ({
      value: account.mailAccountId,
      label: account.name,
    }));

    if (this.accounts.length > 0) {
      this.selectedMailAccountId = this.accounts[0].mailAccountId;
    }
  }

  cancel(): void {
    this.dialogRef.close({ confirmed: false });
  }

  async onAccountChange(mailAccountId: number | string | null): Promise<void> {
    this.selectedMailAccountId =
      typeof mailAccountId === 'number' ? mailAccountId : null;
    if (this.selectedMailAccountId) {
      await this.loadFolders(this.selectedMailAccountId);
    }
  }

  async confirm(): Promise<void> {
    if (!this.selectedMailAccountId || !this.selectedFolderId) {
      return;
    }

    this.saving = true;
    this.cannotSave = false;
    try {
      await this.emailClient.reassign({
        emailIds: this.data.ids,
        mailAccountId: this.selectedMailAccountId,
        mailFolderId: this.selectedFolderId,
      });
      this.dialogRef.close({
        confirmed: true,
        mailAccounts: [this.data.mailAccountId, this.selectedMailAccountId],
      });
    } catch (e) {
      this.cannotSave = true;
      this.matchError.logError(e);
    } finally {
      this.saving = false;
    }
  }
}
